import { HttpClient, type HttpResponse } from "@angular/common/http";
import { type ElementRef, Injectable, inject } from "@angular/core";
import type { UntypedFormGroup } from "@angular/forms";
import {
    iif,
    type Observable,
    of,
    ReplaySubject,
    Subject,
    switchMap,
} from "rxjs";
import { map, tap } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { ActiveControlOptions } from "../../../shared/model/activity-control-options.model";
import { createRequestOption } from "../../../shared/util/request-util";

import type { IClientMessageVM } from "../model/client-message-vm.model";
import type {
    ClientMessageStatus,
    IClientMessage,
} from "../model/client-message.model";
import { ClientMessageQuery } from "../store/client-message.query";
import { ClientMessageStore } from "../store/client-message.store";

@Injectable({
    providedIn: "root",
})
export class ClientMessageService {
    private clientMessageStore = inject(ClientMessageStore);
    private clientMessageQuery = inject(ClientMessageQuery);
    private http = inject(HttpClient);

    reloadSubject = new Subject();
    reload$ = this.reloadSubject.asObservable();
    unreadMessagesCountSubject = new ReplaySubject<number>(1);
    unreadMessagesCount$ = this.unreadMessagesCountSubject.asObservable();
    private resourceUrl = environment.apiUrl + "api/client-messages";
    private readonly LINK_MATCHER: RegExp =
        /(?:(?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$])/gim;

    getClientMessages(req: any): Observable<HttpResponse<IClientMessage[]>> {
        return this.getClientMessagesApiCall(req).pipe(
            tap((res: HttpResponse<IClientMessage[]>) => {
                this.clientMessageStore.set(res.body);
            }),
        );
    }

    getUnreadClientMessagesCount(): Observable<number> {
        return this.http
            .get<number>(`${this.resourceUrl}/unread-count`)
            .pipe(
                tap((count: number) =>
                    this.unreadMessagesCountSubject.next(count),
                ),
            );
    }

    getClientMessageById(id: number): Observable<IClientMessage> {
        return this.clientMessageQuery.selectById(id).pipe(
            switchMap((message) =>
                iif(
                    () => !message,
                    this.getClientMessageByIdApiCall(id),
                    of(message),
                ),
            ),
            tap((m) => {
                if (
                    !m.readByUserId &&
                    (m.fromContactId || m.sendGridInboundSender)
                ) {
                    this.markClientMessageAsRead(m.id).subscribe();
                }
            }),
        );
    }

    sendClientMessage(req: IClientMessageVM): Observable<IClientMessage> {
        return this.http.post<IClientMessage>(this.resourceUrl, req);
    }

    reload(): void {
        this.reloadSubject.next({});
    }

    markClientMessageAsRead(id: number): Observable<IClientMessage> {
        return this.http
            .post<IClientMessage>(`${this.resourceUrl}/${id}/read`, {
                observe: "response",
            })
            .pipe(
                tap(() => {
                    this.getUnreadClientMessagesCount().subscribe();
                }),
            );
    }

    bulkMarkClientMessageAsRead(
        clientMessageIds: number[],
    ): Observable<IClientMessage[]> {
        return this.http
            .post<
                IClientMessage[]
            >(`${this.resourceUrl}/bulk-read`, clientMessageIds)
            .pipe(
                tap(() => {
                    this.getUnreadClientMessagesCount().subscribe();
                }),
            );
    }

    markClientMessageAsUnread(id: number): Observable<IClientMessage> {
        return this.http
            .post<IClientMessage>(`${this.resourceUrl}/${id}/unread`, {
                observe: "response",
            })
            .pipe(
                tap(() => {
                    this.getUnreadClientMessagesCount().subscribe();
                }),
            );
    }

    bulkMarkClientMessageAsUnread(
        clientMessageIds: number[],
    ): Observable<IClientMessage[]> {
        return this.http
            .post<
                IClientMessage[]
            >(`${this.resourceUrl}/bulk-unread`, clientMessageIds)
            .pipe(
                tap(() => {
                    this.getUnreadClientMessagesCount().subscribe();
                }),
            );
    }

    changeClient(
        clientMessageIds: number[],
        clientId: number,
    ): Observable<IClientMessage> {
        return this.http.post<IClientMessage>(
            `${this.resourceUrl}/change-client`,
            { clientMessageIds, clientId },
        );
    }

    bulkChangeClient(
        clientMessageIds: number[],
        clientId: number,
    ): Observable<IClientMessage[]> {
        return this.http.post<IClientMessage[]>(
            `${this.resourceUrl}/bulk-change-client`,
            { clientMessageIds, clientId },
        );
    }

    assignToUser(
        clientMessageIds: number[],
        userId: number,
    ): Observable<IClientMessage> {
        return this.http.post<IClientMessage>(
            `${this.resourceUrl}/assign-to-user`,
            { clientMessageIds, userId },
        );
    }

    bulkAssignToUser(
        clientMessageIds: number[],
        userId: number,
    ): Observable<IClientMessage[]> {
        return this.http.post<IClientMessage[]>(
            `${this.resourceUrl}/bulk-assign-to-user`,
            { clientMessageIds, userId },
        );
    }

    changeStatus(
        clientMessageIds: number[],
        status: ClientMessageStatus,
    ): Observable<IClientMessage> {
        return this.http.post<IClientMessage>(
            `${this.resourceUrl}/change-status`,
            { clientMessageIds, status },
        );
    }

    bulkChangeStatus(
        clientMessageIds: number[],
        status: ClientMessageStatus,
    ): Observable<IClientMessage> {
        return this.http.post<IClientMessage>(
            `${this.resourceUrl}/bulk-change-status`,
            { clientMessageIds, status },
        );
    }

    bulkCreateClientMessage(req: {
        clientIds: number[];
        subject: string;
        message: string;
    }): Observable<IClientMessage[]> {
        return this.http.post<IClientMessage[]>(
            `${this.resourceUrl}/bulk-create`,
            req,
        );
    }

    deleteClientMessage(id: number): Observable<HttpResponse<void>> {
        return this.http.delete<void>(`${this.resourceUrl}/${id}`, {
            observe: "response",
        });
    }

    moveClientMessageToTrash(id: number): Observable<void> {
        return this.http.post<void>(`${this.resourceUrl}/${id}/move-to-trash`, {
            observe: "response",
        });
    }

    removeClientMessageFromTrash(id: number): Observable<void> {
        return this.http.post<void>(
            `${this.resourceUrl}/${id}/remove-from-trash`,
            { observe: "response" },
        );
    }

    bulkDelete(ids: number[]): Observable<HttpResponse<void>> {
        const params = createRequestOption({ ids });
        return this.http.delete<HttpResponse<void>>(
            `${this.resourceUrl}/bulk-delete`,
            { params },
        );
    }

    bulkTrash(ids: number[]): Observable<IClientMessage[]> {
        return this.http.post<IClientMessage[]>(
            `${this.resourceUrl}/bulk-trash`,
            ids,
        );
    }

    bulkUntrash(ids: number[]): Observable<IClientMessage[]> {
        return this.http.post<IClientMessage[]>(
            `${this.resourceUrl}/bulk-untrash`,
            ids,
        );
    }

    bulkAssociateToProject(
        clientMessageIds: number[],
        projectId: number,
    ): Observable<IClientMessage[]> {
        return this.http.post<IClientMessage[]>(
            `${this.resourceUrl}/bulk-associate-project`,
            {
                projectId,
                clientMessageIds,
            },
        );
    }

    addVariablesToMessage(
        variable: string,
        textRef: ElementRef,
        clientMessageForm: UntypedFormGroup,
        activeControl: ActiveControlOptions,
    ): void {
        const textArea = textRef.nativeElement as HTMLTextAreaElement;
        const [startPosition, endPosition] = [
            textArea.selectionStart,
            textArea.selectionEnd,
        ];

        const text = textArea.value || "";
        const newText = `${text.slice(0, startPosition)}${variable}${text.slice(endPosition)}`;

        if (activeControl === ActiveControlOptions.MESSAGE) {
            clientMessageForm.patchValue({
                message: newText,
            });
        } else if (activeControl === ActiveControlOptions.SUBJECT) {
            clientMessageForm.patchValue({
                subject: newText,
            });
        }

        //set caret position at the end of inserted text
        textArea.setSelectionRange(
            startPosition + variable.length,
            startPosition + variable.length,
        );
        textArea.focus();
    }

    createDocumentFromClientMessage(id: number): Observable<void> {
        return this.http.post<void>(
            `${this.resourceUrl}/${id}/create-document`,
            { observe: "response" },
        );
    }

    private getClientMessageByIdApiCall(
        id: number,
    ): Observable<IClientMessage> {
        return this.http.get<IClientMessage>(`${this.resourceUrl}/${id}`).pipe(
            map((message: IClientMessage) => {
                const matches = message.message.match(this.LINK_MATCHER);
                if (matches?.length) {
                    message.links = matches;
                }
                return message;
            }),
        );
    }

    private getClientMessagesApiCall(
        req: any,
    ): Observable<HttpResponse<IClientMessage[]>> {
        const params = createRequestOption(req);
        return this.http
            .get<IClientMessage[]>(this.resourceUrl, {
                params: params,
                observe: "response",
            })
            .pipe(
                map((res) => {
                    (res.body as IClientMessage[]) = res.body.map((message) => {
                        const matches = message.message.match(
                            this.LINK_MATCHER,
                        );
                        if (matches?.length) {
                            message.links = matches;
                        }
                        return message;
                    });
                    return res;
                }),
            );
    }
}
